/**
 * Admin-controlled announcement bar. Reads the `announcement_bar` site setting
 * (via the public settings endpoint). Dismissal is remembered per text version
 * in localStorage, so new text shows again.
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getPublicSettings } from "@/lib/siteSettings";

const LS_KEY = "passadc_annbar_dismissed";

const hashText = (t) => {
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) | 0;
  return String(h);
};

const AnnouncementBar = ({ context = "public" }) => {
  const [bar, setBar] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(null);

  useEffect(() => {
    getPublicSettings().then((s) => setBar(s?.announcement_bar || null)).catch(() => {});
    try { setDismissedVersion(localStorage.getItem(LS_KEY)); } catch { /* ignore */ }
  }, []);

  if (!bar || !bar.enabled || !bar.text) return null;
  const showOn = bar.show_on || "all";
  if (showOn !== "all" && showOn !== context) return null;

  const version = hashText(bar.text || "");
  if (dismissedVersion === version) return null;

  const green = (bar.style || "green") === "green";
  const wrap = green
    ? "bg-success text-white"
    : "bg-secondary text-foreground border-b border-border";

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, version); } catch { /* ignore */ }
    setDismissedVersion(version);
  };

  return (
    <div data-testid="announcement-bar" data-style={green ? "green" : "neutral"} className={`relative z-20 ${wrap}`}>
      <div className="max-w-6xl mx-auto px-4 py-2.5 pr-10 flex items-center justify-center gap-x-3 gap-y-1 text-sm flex-wrap text-center">
        <span className="font-medium">{bar.text}</span>
        {bar.link_url && bar.link_label && (
          <a href={bar.link_url} data-testid="announcement-bar-link" className="underline font-semibold hover:opacity-90 whitespace-nowrap">
            {bar.link_label}
          </a>
        )}
        <button
          onClick={dismiss}
          data-testid="announcement-bar-dismiss"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBar;
